import { Logger } from "../Logger.js";

/*
 Conference observability. Read-only: this module logs and counts, it never repairs.

 lib-jitsi-meet recovers from more than it reports, and the recovery it does best is the one it
 reports least. On a dropped signaling socket it attempts an XEP-0198 stream RESUME -- 3 tries,
 delay random(1500*n, 1000*3^n), so roughly 1.5-3s, then 3-9s, then 4.5-27s -- and while it is
 trying, XmppConnection swallows the strophe DISCONNECTED and returns *before* calling the
 app-facing handler. JitsiConnection therefore emits nothing at all: no event marks the start of
 an outage, and a resume that succeeds is visible only as a second CONNECTION_ESTABLISHED. Every
 conclusion we have drawn about how often the conference drops has been guesswork for that
 reason. This module puts a timestamp on that window, and on the three ways the library hands
 control back to us:

   - GIVE-UP: after the 3rd resume the strophe DISCONNECTED reaches xmpp.js, which sees no
     disconnect of our own in progress and emits CONNECTION_FAILED. (CONNECTION_DISCONNECTED
     fires only when *we* called disconnect(), so a real drop never arrives that way.)
   - ICE: 3 automatic restarts per *page* -- _iceRestarts is never reset, so the budget is
     shared across every outage in the session -- then CONFERENCE_FAILED with ICE_FAILED.
     CONNECTION_INTERRUPTED / CONNECTION_RESTORED bracket the attempts.
   - JWT: on each resume LJM re-parses the token, and past its exp it calls cancelResume() and
     emits CONNECTION_TOKEN_EXPIRED -- and nothing else. No CONNECTION_FAILED follows, so the
     conference simply goes quiet with the UI still showing a healthy session. Our token is 3h.

 Two private fields are read here (_resumeTask, _iceRestarts) on a rolling, 8x8-hosted build, so
 every access is optional-chained; if a library roll renames one we lose that field and nothing
 else. See LOGGING.md for the events this produces.
*/

// -- a flapping link produces interruptions in bursts. Log a bounded number per PAGE (not per
// -- conference -- a session that keeps rebuilding the conference must not reset the cap) and
// -- keep counting the rest, so the counts stay true while the log volume stays sane.
const MAX_LOGGED_EVENTS = 20;

export const ConferenceHealth = {

    _unwatch: [],

    // -- xmpp stream resume, per outage
    _resume_at: 0,
    _resume_attempts: 0,

    // -- jvb ice, per conference
    _ice_down_at: 0,
    _interruptions: 0,

    // -- per page, deliberately never reset (see MAX_LOGGED_EVENTS)
    _resumes_logged: 0,
    _ice_logged: 0,


    // -- once per JitsiConnection, right after it is constructed
    watchConnection(connection){

        if (!connection) return;

        // -- public, and the whole notification: LJM has already cancelled the resume
        this._listen(connection, JitsiMeetJS.events.connection.CONNECTION_TOKEN_EXPIRED, () => {
            Logger.log("Conference", "error", "Jitsi Token Expired", {
                dur_s: Math.round(performance.now() / 1000),
                ...this.takeResumeStats(),
            });
        });

        // -- internal: the only signal that a resume is in flight. `xmpp` is a getter on
        // -- JitsiConnection; `connection` under it is the XmppConnection (a Listenable).
        const xc = connection.xmpp?.connection;
        if (!xc?.addListener) return;

        const EVENT = xc.constructor?.Events?.CONN_STATUS_CHANGED ?? "CONN_STATUS_CHANGED";
        // -- ExtendedStatus, deliberately numbered past strophe's own 0-12
        const RESUMING = xc.constructor?.Status?.RESUMING ?? 15;

        this._listen(xc, EVENT, status => {

            if (status !== RESUMING) return;

            // -- first attempt of this outage owns the clock; retries land on the same window
            if (!this._resume_at) this._resume_at = Date.now();
            this._resume_attempts++;

            if (++this._resumes_logged > MAX_LOGGED_EVENTS) return;

            Logger.log("Conference", "warn", "Jitsi Connection Resuming", {
                attempt: xc._resumeTask?.retryCount ?? this._resume_attempts,
                delay_ms: xc._resumeTask?.retryDelay,
                since_last_success_ms: xc.getTimeSinceLastSuccess?.(),
                last_failure: xc.getLastFailedMessage?.()?.slice?.(0, 200),
            });
        });
    },


    // -- once per JitsiConference, right after initJitsiConference
    watchConference(conference){

        if (!conference) return;

        const E = JitsiMeetJS.events.conference;

        this._listen(conference, E.CONNECTION_INTERRUPTED, () => {
            this._ice_down_at = Date.now();
            this._interruptions++;
            if (++this._ice_logged > MAX_LOGGED_EVENTS) return;
            Logger.log("Conference", "warn", "Conference Media Interrupted", {
                interruptions: this._interruptions,
                ice_restarts: this.iceRestarts(conference),
            });
        });

        this._listen(conference, E.CONNECTION_RESTORED, () => {
            const down_ms = this._ice_down_at ? Date.now() - this._ice_down_at : null;
            this._ice_down_at = 0;
            if (this._ice_logged > MAX_LOGGED_EVENTS) return;
            Logger.log("Conference", "info", "Conference Media Restored", {
                down_ms: down_ms,
                interruptions: this._interruptions,
                ice_restarts: this.iceRestarts(conference),
            });
        });
    },


    /*
     How long the signaling socket has been down and how many resume attempts LJM has spent on
     it, consumed by whichever outcome arrives first: the second CONNECTION_ESTABLISHED (resume
     succeeded), CONNECTION_FAILED (budget exhausted), or CONNECTION_TOKEN_EXPIRED (resume
     cancelled). Returns an empty object when no resume was in flight -- e.g. the first connect,
     or a failure that never got as far as scheduling one -- so it can always be spread into a
     log without inventing fields.
    */
    takeResumeStats(){

        if (!this._resume_at)
            return {};

        const stats = {down_ms: Date.now() - this._resume_at, resume_attempts: this._resume_attempts};
        this._resume_at = 0;
        this._resume_attempts = 0;
        return stats;
    },


    // -- LJM's per-page ICE restart budget, spent 3 and out (never reset by the library). A
    // -- fresh JitsiConference gets a fresh count, which is the argument for rejoining rather
    // -- than following LJM's "force reload" hint on ICE_FAILED.
    iceRestarts(conference){
        return conference?._iceRestarts;
    },


    // -- called from the conference teardown, before the objects are dropped
    stop(){

        for (const unwatch of this._unwatch){
            try { unwatch(); }
            catch (e){ console.warn("[PL-ConferenceHealth] Unwatch failed", e); }
        }

        this._unwatch.length = 0;
        this._resume_at = 0;
        this._resume_attempts = 0;
        this._ice_down_at = 0;
        this._interruptions = 0;
    },


    _listen(target, event, handler){

        if (!event) return;

        /*
         Contained, and that is not paranoia. These listeners run SYNCHRONOUSLY inside the
         library's own emits -- CONN_STATUS_CHANGED fires from the middle of
         _stropheConnectionCb -- and lib-jitsi-meet's EventEmitter lets a throwing listener
         escape emit() and starve every listener registered after it (verified against
         ddc6ad7246). Purely observational code must not be able to break the connection
         callback, so nothing in here is allowed out.

         The same property explains a log that can go missing: the JitsiConnection constructor
         registers its own RESUMING listener (the JWT expiry check) BEFORE ours, and it throws
         on a token it cannot parse -- in which case "Jitsi Connection Resuming" never fires.
        */
        const guarded = (...args) => {
            try { handler(...args); }
            catch (e){ console.warn("[PL-ConferenceHealth] Listener failed", event, e); }
        };

        // -- Listenable hands back its own unsubscribe; JitsiConnection is not one (it forwards
        // -- to the xmpp module) and only has the addEventListener pair
        if (target.addCancellableListener)
            this._unwatch.push(target.addCancellableListener(event, guarded));

        else if (target.addEventListener){
            target.addEventListener(event, guarded);
            this._unwatch.push(() => target.removeEventListener(event, guarded));
        }
    },
};
